/*
 * Génération LOCALE du bilan CYCLIQ en PDF (conservation nulle).
 * Portage navigateur du générateur validé en Phase 1 : pdf lib + Poppins.
 * Le PDF en clair ne quitte JAMAIS le navigateur. La version envoyée par
 * email est chiffrée AES 256 avec le code personnel avant toute transmission.
 *
 * Règle éditoriale : aucun tiret cadratin, demi cadratin ou trait d'union
 * dans les textes visibles du document.
 */

import { PDFDocument, rgb, PDFName, PDFString } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { encryptPDF } from "@pdfsmaller/pdf-encrypt";

const A4 = [595.28, 841.89];

const hex = (h) => rgb(
  parseInt(h.slice(1, 3), 16) / 255,
  parseInt(h.slice(3, 5), 16) / 255,
  parseInt(h.slice(5, 7), 16) / 255,
);

const NAVY = hex("#235277");
const NAVY_DARK = hex("#1E4767");
const LEMON = hex("#EAFE63");
const INK = hex("#060606");
const GREY = hex("#9E9E9E");
const SOFT = hex("#E2F6FF");
const PAPER = hex("#F9FEFF");
const WHITE = hex("#FFFFFF");
const GREEN_DARK = hex("#114E0B");
const GREEN_BG = hex("#EFF7EE");
const CAUTION_BG = hex("#FDF6E7");
const CAUTION_TXT = hex("#5E5515");
const HEADER_SOFT = hex("#DDEBF4");
const HEADER_MUTED = hex("#BFD3E2");
const CTA_MUTED = hex("#CFE0EC");
const TRACK = hex("#D8E8F2");

const TAGLINE = "Comprenez vos cycles. Vivez enfin à votre rythme.";

const MARGIN = 46;

/* ----- Ressources locales (polices et logo servis par le frontend) ----- */

let assetsPromise = null;

async function fetchBytes(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Ressource indisponible : ${url}`);
  return new Uint8Array(await res.arrayBuffer());
}

function loadAssets() {
  if (!assetsPromise) {
    assetsPromise = Promise.all([
      fetchBytes("/fonts/Poppins-Regular.ttf"),
      fetchBytes("/fonts/Poppins-SemiBold.ttf"),
      fetchBytes("/fonts/Poppins-Bold.ttf"),
      fetchBytes("/logo_blanc.png"),
    ]).catch((e) => { assetsPromise = null; throw e; });
  }
  return assetsPromise;
}

/* ----- Aides de mise en page ----- */

function wrapText(text, font, size, maxWidth) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
      line = candidate;
    } else {
      if (line) lines.push(line);
      line = word;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundedRect(page, x, y, w, h, r, color) {
  const radius = Math.min(r, w / 2, h / 2);
  page.drawRectangle({ x: x + radius, y, width: w - 2 * radius, height: h, color });
  page.drawRectangle({ x, y: y + radius, width: w, height: h - 2 * radius, color });
  const corners = [
    [x + radius, y + radius],
    [x + w - radius, y + radius],
    [x + radius, y + h - radius],
    [x + w - radius, y + h - radius],
  ];
  for (const [cx, cy] of corners) {
    page.drawCircle({ x: cx, y: cy, size: radius, color });
  }
}

class Doc {
  constructor(pdfDoc, fonts) {
    this.pdfDoc = pdfDoc;
    this.fonts = fonts;
    this.w = A4[0];
    this.h = A4[1];
    this.pageNumber = 1;
    this.page = pdfDoc.addPage(A4);
    this.page.drawRectangle({ x: 0, y: 0, width: this.w, height: this.h, color: PAPER });
    this.y = this.h;
  }

  get contentW() {
    return this.w - 2 * MARGIN;
  }

  footer() {
    this.page.drawText("Gino Body & Soul", { x: MARGIN, y: 34, size: 7.5, font: this.fonts.regular, color: GREY });
    const label = `Page ${this.pageNumber}`;
    const width = this.fonts.regular.widthOfTextAtSize(label, 7.5);
    this.page.drawText(label, { x: this.w - MARGIN - width, y: 34, size: 7.5, font: this.fonts.regular, color: GREY });
  }

  ensure(needed) {
    if (this.y - needed < 64) {
      this.footer();
      this.pageNumber += 1;
      this.page = this.pdfDoc.addPage(A4);
      this.page.drawRectangle({ x: 0, y: 0, width: this.w, height: this.h, color: PAPER });
      this.y = this.h - 56;
    }
  }

  spacer(amount) {
    this.y -= amount;
  }

  text(str, x, y, font, size, color) {
    this.page.drawText(String(str), { x, y, size, font, color });
  }

  textCentered(str, cx, y, font, size, color) {
    const width = font.widthOfTextAtSize(String(str), size);
    this.text(str, cx - width / 2, y, font, size, color);
  }

  textRight(str, xRight, y, font, size, color) {
    const width = font.widthOfTextAtSize(String(str), size);
    this.text(str, xRight - width, y, font, size, color);
  }

  textBlock(str, font, size, color, { leading = null, maxWidth = null, gap = 6 } = {}) {
    const mw = maxWidth || this.contentW;
    const lh = leading || size * 1.45;
    const lines = wrapText(str, font, size, mw);
    this.ensure(lines.length * lh + gap);
    for (const line of lines) {
      this.y -= lh;
      this.text(line, MARGIN, this.y, font, size, color);
    }
    this.y -= gap;
    return lines;
  }

  sectionTitle(label) {
    this.ensure(46);
    this.spacer(14);
    this.page.drawRectangle({ x: MARGIN, y: this.y - 15, width: 3.5, height: 15, color: LEMON });
    this.text(label, MARGIN + 11, this.y - 12.5, this.fonts.bold, 12.5, INK);
    this.y -= 26;
  }
}

function formatDateFr(isoString) {
  const months = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
    "août", "septembre", "octobre", "novembre", "décembre"];
  try {
    const d = isoString ? new Date(isoString) : new Date();
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch (e) {
    return "";
  }
}

/* ------------------------------------------------------------------ */
/* Génération complète du bilan                                         */
/* ------------------------------------------------------------------ */

/* ----- Sections du document (extraites pour lisibilite et maintenance) ----- */

function renderHeaderBand(ctx) {
  const { d, fonts, logo, created } = ctx;
  const M = MARGIN;
  const bandH = 118;
  d.page.drawRectangle({ x: 0, y: d.h - bandH, width: d.w, height: bandH, color: NAVY });
  d.page.drawCircle({ x: d.w - 60, y: d.h - 16, size: 78, color: NAVY_DARK });

  const logoW = 118;
  const logoH = 118 * (596 / 1377);
  d.page.drawImage(logo, { x: M, y: d.h - 30 - logoH, width: logoW, height: logoH });
  d.text(TAGLINE, M, d.h - 40 - logoH, fonts.regular, 7.8, HEADER_SOFT);

  d.textRight("Bilan CYCLIQ", d.w - M, d.h - 52, fonts.bold, 15, WHITE);
  d.textRight(created ? `Établi le ${created}` : "Bilan personnalisé", d.w - M, d.h - 68, fonts.regular, 9, HEADER_MUTED);

  d.y = d.h - bandH - 30;
}

function renderProfileTitle(ctx) {
  const { d, fonts, prenom, profile } = ctx;
  const title = prenom ? `${prenom}, ton profil CYCLIQ` : "Ton profil CYCLIQ";
  d.textBlock(title, fonts.semiBold, 12, GREY, { gap: 2 });
  d.textBlock(profile.label || "", fonts.bold, 21, NAVY, { gap: 10 });
}

function renderScoreIntro(ctx) {
  const { d, fonts, result } = ctx;
  const M = MARGIN;
  const circleR = 37;
  const introX = M + 2 * circleR + 24;
  const introW = d.contentW - 2 * circleR - 24;
  const introLines = wrapText(result.intro_text || "", fonts.regular, 9.5, introW);
  const blockH = Math.max(2 * circleR, introLines.length * 14) + 8;
  d.ensure(blockH + 16);
  const top = d.y;

  const cy = top - circleR;
  d.page.drawCircle({ x: M + circleR, y: cy, size: circleR, color: WHITE, borderColor: LEMON, borderWidth: 4 });
  const score = result.global_score;
  d.textCentered(score !== null && score !== undefined ? String(score) : "…", M + circleR, cy - 4, fonts.bold, 24, NAVY);
  d.textCentered("sur 100", M + circleR, cy - 18, fonts.regular, 8, GREY);
  d.textCentered("Score d'orientation", M + circleR, top - 2 * circleR - 12, fonts.semiBold, 7.5, NAVY);

  let ty = top - 12;
  for (const line of introLines) {
    d.text(line, introX, ty, fonts.regular, 9.5, INK);
    ty -= 14;
  }
  d.y = top - blockH - 18;
}

function renderMedicalCaution(ctx) {
  const { d, fonts } = ctx;
  const M = MARGIN;
  const caution = "Un professionnel de santé encadre actuellement ta pratique. Avant toute modification "
    + "de ton activité physique, sollicite son avis. Ce bilan ne contient volontairement "
    + "aucune recommandation sportive.";
  const lines = wrapText(caution, fonts.regular, 8.8, d.contentW - 28);
  const boxH = lines.length * 12.5 + 34;
  d.ensure(boxH + 10);
  roundedRect(d.page, M, d.y - boxH, d.contentW, boxH, 8, CAUTION_BG);
  d.text("Une précaution importante", M + 14, d.y - 20, fonts.bold, 9.5, CAUTION_TXT);
  let yy = d.y - 34;
  for (const line of lines) {
    d.text(line, M + 14, yy, fonts.regular, 8.8, CAUTION_TXT);
    yy -= 12.5;
  }
  d.y -= boxH + 12;
}

function renderDimensions(ctx) {
  const { d, fonts, result } = ctx;
  const M = MARGIN;
  d.sectionTitle("Tes cinq dimensions");
  const barTrackX = M + 190;
  const barW = d.contentW - 190 - 46;
  for (const dim of result.dimensions || []) {
    d.ensure(24);
    d.y -= 17;
    d.text(dim.label || "", M, d.y, fonts.regular, 9, INK);
    roundedRect(d.page, barTrackX, d.y - 1.5, barW, 8, 4, TRACK);
    const s = dim.score;
    if (s !== null && s !== undefined) {
      roundedRect(d.page, barTrackX, d.y - 1.5, Math.max(8, (barW * Math.min(Math.max(s, 0), 100)) / 100.0), 8, 4, NAVY);
    }
    d.textRight(s !== null && s !== undefined ? `${s}/100` : "…", d.w - M, d.y, fonts.semiBold, 9, NAVY);
  }
  d.y -= 10;
}

function renderCardsSection(ctx, sectionLabel, items, bg, accent) {
  const { d, fonts } = ctx;
  const M = MARGIN;
  d.sectionTitle(sectionLabel);
  for (const item of items || []) {
    const body = wrapText(item.text || "", fonts.regular, 8.6, d.contentW - 28);
    const boxH = body.length * 12 + 34;
    d.ensure(boxH + 8);
    roundedRect(d.page, M, d.y - boxH, d.contentW, boxH, 8, bg);
    d.text(item.label || "", M + 14, d.y - 19, fonts.bold, 9.5, accent);
    const sc = item.score;
    if (sc !== null && sc !== undefined) {
      d.textRight(`${sc}/100`, d.w - M - 14, d.y - 19, fonts.bold, 9.5, accent);
    }
    let yy = d.y - 33;
    for (const line of body) {
      d.text(line, M + 14, yy, fonts.regular, 8.6, INK);
      yy -= 12;
    }
    d.y -= boxH + 8;
  }
}

function renderActionPlan(ctx) {
  const { d, fonts, result, medicalCaution } = ctx;
  const M = MARGIN;
  d.sectionTitle(medicalCaution ? "Tes prochaines étapes adaptées" : "Ton plan d'action");
  let actionIndex = 0;
  for (const action of result.actions || []) {
    actionIndex += 1;
    const lines = wrapText(action, fonts.regular, 9, d.contentW - 36);
    const need = lines.length * 13 + 14;
    d.ensure(need);
    d.y -= 15;
    d.page.drawCircle({ x: M + 8, y: d.y + 3, size: 8, color: NAVY });
    d.textCentered(String(actionIndex), M + 8, d.y, fonts.bold, 8.5, LEMON);
    let yy = d.y;
    for (const line of lines) {
      d.text(line, M + 26, yy, fonts.regular, 9, INK);
      yy -= 13;
    }
    d.y = yy + 13 - 8;
  }
  if (medicalCaution) {
    d.spacer(4);
    d.textBlock(
      "Ces étapes t'aident à préparer l'échange avec le professionnel de santé qui suit ta situation. "
      + "Elles ne contiennent aucune recommandation sportive ni prescription.",
      fonts.regular, 7.8, GREY,
    );
  }
}

function renderLimitsAndPrivacy(ctx) {
  const { d, fonts, noCycle } = ctx;
  d.sectionTitle("Les limites de ce bilan");
  let limits = "Ce bilan est un score d'orientation construit à partir de tes réponses aux dimensions CYCLIQ. "
    + "Il ne constitue ni une mesure clinique, ni un diagnostic médical, ni une évaluation psychologique "
    + "validée. Il ne remplace pas l'avis d'un médecin ou d'un professionnel de santé qualifié.";
  if (noCycle) {
    limits += " Ton bilan est construit sur tes variations individuelles, sans référence à une phase de cycle.";
  }
  d.textBlock(limits, fonts.regular, 8.8, INK);

  d.textBlock(
    "Ce document a été généré dans ton navigateur. Tes réponses et tes résultats ne sont conservés "
    + "ni par CYCLIQ, ni par ses prestataires. Ce fichier est ta seule copie : garde le précieusement.",
    fonts.regular, 7.8, GREY,
  );
}

function renderCta(ctx) {
  const { d, fonts, pdfDoc, bookingLink } = ctx;
  const M = MARGIN;
  const ctaH = 96;
  d.ensure(ctaH + 20);
  d.spacer(12);
  roundedRect(d.page, M, d.y - ctaH, d.contentW, ctaH, 12, NAVY);
  d.textCentered("Donne du sens à ton bilan", d.w / 2, d.y - 26, fonts.bold, 12.5, WHITE);
  d.textCentered("Un échange avec Gino pour comprendre tes résultats, préciser tes priorités", d.w / 2, d.y - 41, fonts.regular, 8.6, CTA_MUTED);
  d.textCentered("et vérifier si un accompagnement GBS est adapté à ta situation.", d.w / 2, d.y - 53, fonts.regular, 8.6, CTA_MUTED);
  const btnW = 232;
  const btnH = 26;
  const bx = (d.w - btnW) / 2;
  const by = d.y - ctaH + 14;
  roundedRect(d.page, bx, by, btnW, btnH, 13, LEMON);
  d.textCentered("Interpréter mon bilan avec Gino", d.w / 2, by + 8.5, fonts.bold, 10, INK);

  if (bookingLink) {
    const annotation = pdfDoc.context.obj({
      Type: PDFName.of("Annot"),
      Subtype: PDFName.of("Link"),
      Rect: [M, d.y - ctaH, M + d.contentW, d.y],
      Border: [0, 0, 0],
      A: {
        Type: PDFName.of("Action"),
        S: PDFName.of("URI"),
        URI: PDFString.of(bookingLink),
      },
    });
    const annotationRef = pdfDoc.context.register(annotation);
    d.page.node.set(PDFName.of("Annots"), pdfDoc.context.obj([annotationRef]));
  }
  d.y -= ctaH + 10;

  if (bookingLink) {
    d.textBlock(`Lien de réservation : ${bookingLink}`, fonts.regular, 7.2, GREY, { gap: 2 });
  }
}

function renderReferences(ctx) {
  const { d, fonts, journeyRef, baremeVersion } = ctx;
  let ref = journeyRef ? `Référence du parcours : ${journeyRef}` : "";
  if (baremeVersion) {
    ref += `${ref ? "  |  " : ""}Version du barème : ${baremeVersion}`;
  }
  if (ref) {
    d.textBlock(ref, fonts.regular, 7.2, GREY, { gap: 0 });
  }
}

export async function generateBilanPdf(result, bookingLink, { prenom = "", baremeVersion = "", journeyRef = "" } = {}) {
  const [regularBytes, semiBoldBytes, boldBytes, logoBytes] = await loadAssets();

  const pdfDoc = await PDFDocument.create();
  pdfDoc.registerFontkit(fontkit);
  const fonts = {
    regular: await pdfDoc.embedFont(regularBytes, { subset: true }),
    semiBold: await pdfDoc.embedFont(semiBoldBytes, { subset: true }),
    bold: await pdfDoc.embedFont(boldBytes, { subset: true }),
  };
  const logo = await pdfDoc.embedPng(logoBytes);

  const d = new Doc(pdfDoc, fonts);
  const flags = result.flags || {};
  const ctx = {
    d,
    fonts,
    pdfDoc,
    logo,
    result,
    bookingLink,
    prenom,
    baremeVersion,
    journeyRef,
    profile: result.profile || {},
    medicalCaution: Boolean(flags.medical_caution),
    noCycle: Boolean(flags.no_cycle),
    created: formatDateFr(result.created_at),
  };

  renderHeaderBand(ctx);
  renderProfileTitle(ctx);
  renderScoreIntro(ctx);
  if (ctx.medicalCaution) renderMedicalCaution(ctx);
  renderDimensions(ctx);
  renderCardsSection(ctx, "Tes forces", result.forces, GREEN_BG, GREEN_DARK);
  renderCardsSection(ctx, "Tes priorités", result.priorites, SOFT, NAVY);
  renderActionPlan(ctx);
  renderLimitsAndPrivacy(ctx);
  renderCta(ctx);
  renderReferences(ctx);

  d.footer();
  return pdfDoc.save({ useObjectStreams: false });
}

/* ------------------------------------------------------------------ */
/* Chiffrement local AES 256 avec le code personnel                     */
/* ------------------------------------------------------------------ */

export async function encryptBilanPdf(pdfBytes, personalCode) {
  if (!personalCode || String(personalCode).length < 6) {
    throw new Error("Code personnel manquant : chiffrement impossible.");
  }
  const encrypted = await encryptPDF(new Uint8Array(pdfBytes), String(personalCode), {
    algorithm: "AES-256",
    allowPrinting: true,
    allowCopying: false,
    allowModifying: false,
  });
  if (!encrypted || encrypted.length === 0) {
    throw new Error("Le chiffrement du bilan a échoué.");
  }
  return encrypted;
}

/* ----- Téléchargement local, sans aucun passage serveur ----- */

export function downloadPdf(pdfBytes, filename = "Bilan_CYCLIQ.pdf") {
  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

/* ----- Encodage base64 pour transmettre le PDF déjà chiffré ----- */

export function bytesToBase64(bytes) {
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
